import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button, 
  CircularProgress,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Chip,
  Avatar,
  Container,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Drawer
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CancelIcon from '@mui/icons-material/Cancel';
import QuizIcon from '@mui/icons-material/Quiz';
import HomeIcon from '@mui/icons-material/Home';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import api, { 
  getCourse, 
  updateProgress, 
  getUserTestHistory
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { selectQuestionsForTest } from '../utils/testUtils';

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  info: '#74b9ff',
  purple: '#6c5ce7',
  orange: '#e17055',
  dark: '#2d3436',
  light: '#dfe6e9'
};

// Helper seguro para alpha
const safeAlpha = (color, opacity) => {
  if (!color || typeof color !== 'string') return `rgba(116, 185, 255, ${opacity})`;
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return color;
};

// Constantes de layout
const SIDEBAR_WIDTH = 280;

const CourseViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [course, setCourse] = useState(null);
  const [activeLesson, setActiveLesson] = useState(0);
  const [completed, setCompleted] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [testAnswers, setTestAnswers] = useState({});
  const [testCompleted, setTestCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [testScore, setTestScore] = useState(0);
  const contentRef = useRef(null);
  const [selectedTestQuestions, setSelectedTestQuestions] = useState([]);
  const [testHistory, setTestHistory] = useState({ attempts: [] });
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        const [courseData, testHistoryData] = await Promise.all([
          getCourse(id),
          user ? getUserTestHistory(user.userId) : { attempts: [] }
        ]);
  
        // Filtrar solo los intentos para este curso
        const courseTestHistory = testHistoryData?.attempts?.filter(
          attempt => attempt.course_id === parseInt(id)
        ) || [];
  
        setTestHistory({ attempts: courseTestHistory });
  
        // Procesar las lecciones
        let lessons = [];
        if (Array.isArray(courseData.lessons)) {
          lessons = courseData.lessons;
        } else if (courseData.lessons && typeof courseData.lessons === 'object') {
          lessons = Object.values(courseData.lessons).map((lesson) => ({
            title: lesson.title || '',
            content: lesson.content || '',
          }));
        }
  
        // Procesar el test y la configuración
        const testConfig = {
          numberOfQuestions: courseData.questions_per_test || courseData.test_config?.questions_per_test || 10,
          rotateQuestions: courseData.test_config?.rotateQuestions || true,
          requireAllDifficulties: courseData.test_config?.requireAllDifficulties || false
        };
  
        // Obtener preguntas del test
        let testQuestions = [];
        if (courseData.test && courseData.test.questions) {
          testQuestions = Array.isArray(courseData.test.questions) ? courseData.test.questions : [];
        }
  
        // Aplicar la configuración para seleccionar las preguntas
        let selectedQuestions = testQuestions;
        if (testConfig.rotateQuestions && testQuestions.length > 0) {
          selectedQuestions = selectQuestionsForTest(
            testQuestions, 
            testConfig,
            courseTestHistory.map(attempt => ({
              questionId: attempt.question_id,
              correct: attempt.correct
            }))
          );
        }
  
        setSelectedTestQuestions(selectedQuestions);
  
        setCourse({
          ...courseData,
          lessons: lessons,
          test: {
            questions: selectedQuestions,
            config: testConfig
          }
        });
  
        setError(null);
      } catch (err) {
        console.error('Error fetching course:', err);
        setError('Error al cargar el curso. Por favor, intenta de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };
  
    fetchCourse();
  }, [id, user]);

  // Función handleComplete del archivo viejo (funcional)
  const handleComplete = async () => {
    const newCompleted = { ...completed, [activeLesson]: true };
    setCompleted(newCompleted);
  
    if (!user) {
      console.error('Usuario no autenticado');
      return;
    }
  
    try {
      const completedLessonsCount = Object.keys(newCompleted).length;
      const allLessonsCompleted = completedLessonsCount === course.lessons.length;
      
      await updateProgress(user.userId, id, { 
        completed_lessons: completedLessonsCount,
        completed: allLessonsCompleted,
        certificate_issued: false
      });
    } catch (error) {
      console.error('Error updating progress:', error);
    }

    if (activeLesson === course.lessons.length - 1) {
      setActiveLesson(course.lessons.length);
    } else {
      setActiveLesson(activeLesson + 1);
    }
  
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };

  const handleLessonClick = (index) => {
    setActiveLesson(index);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
    setTestAnswers({...testAnswers, [currentQuestion]: answer});
  };

  // Función handleTestComplete del archivo viejo (funcional)
  const handleTestComplete = async () => {
    const totalQuestions = selectedTestQuestions.length;
    const correctAnswers = selectedTestQuestions.filter((q, index) => {
      const answer = testAnswers[index];
      const correctAnswer = q.correct_answer || q.correctAnswer;
      return answer === correctAnswer;
    }).length;
  
    const score = (correctAnswers / totalQuestions) * 100;
    setTestScore(score);
    setTestCompleted(true);
    setShowResults(true);
  
    try {
      const testAttempt = {
        userId: user.userId,
        questions: selectedTestQuestions,
        answers: selectedTestQuestions.map((question, index) => ({
          questionId: question.id,
          userAnswer: testAnswers[index],
          isCorrect: testAnswers[index] === (question.correct_answer || question.correctAnswer)
        })),
        score,
        passed: score >= course.min_passing_score
      };
  
      await api.post(`/courses/${id}/test-attempts`, testAttempt);
  
      if (score >= course.min_passing_score) {
        const progressData = { 
          completed_lessons: course.lessons.length,
          test_score: score,
          completed: true,
          certificate_issued: true
        };
        await updateProgress(user.userId, id, progressData);
      } else {
        const progressData = {
          completed_lessons: course.lessons.length,
          test_score: score,
          completed: false,
          certificate_issued: false
        };
        await updateProgress(user.userId, id, progressData);
      }
    } catch (error) {
      console.error('Error al guardar el intento:', error);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestion < course.test.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer('');
    } else {
      handleTestComplete();
    }
  };

  const renderTestQuestion = () => {
    if (!course?.test?.questions || course.test.questions.length === 0) {
      return (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '2px solid #e0e0e0', textAlign: 'center' }}>
          <QuizIcon sx={{ fontSize: 64, color: '#bdbdbd', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#636e72' }}>
            No hay preguntas disponibles para este test
          </Typography>
        </Paper>
      );
    }

    const currentQ = course.test.questions[currentQuestion];
    if (!currentQ) return null;

    return (
      <Box>
        {/* Progress del test */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: safeAlpha(COLORS.purple, 0.05), border: `2px solid ${safeAlpha(COLORS.purple, 0.2)}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontWeight: 600, color: COLORS.purple }}>
              Pregunta {currentQuestion + 1} de {course.test.questions.length}
            </Typography>
            <Chip 
              label={`${Math.round(((currentQuestion + 1) / course.test.questions.length) * 100)}%`}
              size="small"
              sx={{ bgcolor: COLORS.purple, color: 'white', fontWeight: 700 }}
            />
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={((currentQuestion + 1) / course.test.questions.length) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: safeAlpha(COLORS.purple, 0.2),
              '& .MuiLinearProgress-bar': {
                backgroundColor: COLORS.purple,
                borderRadius: 4
              }
            }}
          />
        </Paper>

        {/* Pregunta */}
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '2px solid #e0e0e0' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.dark, mb: 4 }}>
            {currentQ.question}
          </Typography>

          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <RadioGroup value={selectedAnswer} onChange={(e) => handleAnswerSelect(e.target.value)}>
              {currentQ.options?.map((option, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    border: selectedAnswer === option 
                      ? `2px solid ${COLORS.primary}` 
                      : '2px solid #e0e0e0',
                    bgcolor: selectedAnswer === option ? safeAlpha(COLORS.primary, 0.05) : 'white',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: safeAlpha(COLORS.primary, 0.5),
                      bgcolor: safeAlpha(COLORS.primary, 0.02)
                    }
                  }}
                >
                  <FormControlLabel
                    value={option}
                    control={<Radio sx={{ color: '#9e9e9e', '&.Mui-checked': { color: COLORS.primary } }} />}
                    label={<Typography sx={{ fontWeight: selectedAnswer === option ? 600 : 400, py: 1 }}>{option}</Typography>}
                    sx={{ m: 0, px: 2, py: 0.5, width: '100%' }}
                  />
                </Paper>
              ))}
            </RadioGroup>
          </FormControl>
        </Paper>
      </Box>
    );
  };

  const handleCloseResults = () => {
    setShowResults(false);
    navigate('/courses');
  };

  const handleViewCertificate = () => {
    navigate('/profile');
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: 'linear-gradient(180deg, #f8fffe 0%, #f0f7ff 100%)' }}>
        <Box sx={{ width: 80, height: 80, borderRadius: 3, background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, boxShadow: `0 8px 32px ${safeAlpha(COLORS.gradient1, 0.3)}` }}>
          <MenuBookIcon sx={{ fontSize: 40, color: 'white' }} />
        </Box>
        <Typography variant="h6" sx={{ color: COLORS.dark, fontWeight: 600, mb: 2 }}>Cargando curso...</Typography>
        <CircularProgress sx={{ color: COLORS.primary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography color="error" variant="h5">{error}</Typography>
        <Button variant="contained" onClick={() => navigate('/courses')} sx={{ mt: 3, background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)` }}>
          Volver a los cursos
        </Button>
      </Container>
    );
  }

  if (!course) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5">No se encontró el curso.</Typography>
        <Button variant="contained" onClick={() => navigate('/courses')} sx={{ mt: 3, background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)` }}>
          Volver a los cursos
        </Button>
      </Container>
    );
  }

  const totalSteps = course.lessons.length + 1;
  const completedSteps = Object.keys(completed).length + (testCompleted ? 1 : 0);
  const progress = (completedSteps / totalSteps) * 100;

  // Sidebar Content (reutilizable)
  const sidebarContent = (
    <>
      <Box sx={{ p: 2, borderBottom: '2px solid #e0e0e0' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.dark, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Contenido del curso
        </Typography>
      </Box>
      <List sx={{ p: 1, overflowY: 'auto', flex: 1 }}>
        {course.lessons.map((lesson, index) => {
          const isActive = activeLesson === index;
          const isCompleted = completed[index];
          
          return (
            <ListItem 
              button 
              key={index} 
              onClick={() => handleLessonClick(index)}
              sx={{
                mb: 0.5,
                borderRadius: 2,
                border: isActive ? `2px solid ${COLORS.primary}` : '2px solid transparent',
                bgcolor: isActive ? safeAlpha(COLORS.primary, 0.08) : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: safeAlpha(COLORS.primary, 0.05), borderColor: safeAlpha(COLORS.primary, 0.3) }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {isCompleted ? (
                  <CheckCircleOutlineIcon sx={{ color: COLORS.success }} />
                ) : isActive ? (
                  <PlayCircleFilledIcon sx={{ color: COLORS.primary }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ color: '#bdbdbd' }} />
                )}
              </ListItemIcon>
              <ListItemText 
                primary={lesson.title || `Lección ${index + 1}`}
                primaryTypographyProps={{ 
                  sx: { 
                    fontWeight: isActive ? 700 : 500, 
                    color: isActive ? COLORS.primary : COLORS.dark, 
                    fontSize: '0.9rem',
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  } 
                }}
              />
            </ListItem>
          );
        })}
        
        {/* Test Final */}
        <ListItem 
          button 
          onClick={() => handleLessonClick(course.lessons.length)}
          sx={{
            mt: 1,
            borderRadius: 2,
            border: activeLesson === course.lessons.length ? `2px solid ${COLORS.purple}` : '2px solid transparent',
            bgcolor: activeLesson === course.lessons.length ? safeAlpha(COLORS.purple, 0.08) : safeAlpha(COLORS.purple, 0.03),
            '&:hover': { bgcolor: safeAlpha(COLORS.purple, 0.1) }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <AssignmentIcon sx={{ color: activeLesson === course.lessons.length ? COLORS.purple : '#9e9e9e' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Test Final"
            secondary={`Mín. ${course.min_passing_score || 70}%`}
            primaryTypographyProps={{ sx: { fontWeight: activeLesson === course.lessons.length ? 700 : 500, color: activeLesson === course.lessons.length ? COLORS.purple : COLORS.dark } }}
            secondaryTypographyProps={{ sx: { color: '#9e9e9e', fontSize: '0.8rem' } }}
          />
        </ListItem>
      </List>
    </>
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh', 
      bgcolor: '#f8fffe',
      overflow: 'hidden',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    }}>
      {/* ========== HEADER ========== */}
      <Box 
        sx={{ 
          background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
          p: 1.5,
          flexShrink: 0,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }}
      >
        <Container maxWidth="xl" sx={{ height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Botón menú móvil */}
              {isMobile && (
                <Tooltip title={sidebarOpen ? "Ocultar menú" : "Mostrar menú"}>
                  <IconButton 
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                  >
                    {sidebarOpen ? <CloseIcon /> : <MenuIcon />}
                  </IconButton>
                </Tooltip>
              )}
              
              {/* Botón home */}
              <Tooltip title="Volver a mis cursos">
                <IconButton 
                  onClick={() => navigate('/mis-cursos')}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                >
                  <HomeIcon />
                </IconButton>
              </Tooltip>
              
              <Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 800, lineHeight: 1.2 }}>
                  {course.title}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>
                  {course.lessons.length} lecciones + Test final
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Barra de progreso */}
              <Box sx={{ minWidth: 180 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.8rem', fontWeight: 600 }}>
                    Progreso
                  </Typography>
                  <Typography sx={{ color: 'white', fontSize: '0.8rem', fontWeight: 700 }}>
                    {Math.round(progress)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={progress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    '& .MuiLinearProgress-bar': { backgroundColor: 'white', borderRadius: 4 }
                  }}
                />
              </Box>
              
              <Chip
                label={`${completedSteps}/${totalSteps}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ========== ÁREA PRINCIPAL ========== */}
      <Box sx={{ 
        display: 'flex', 
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Sidebar Desktop */}
        {!isMobile && sidebarOpen && (
          <Paper
            elevation={0}
            sx={{ 
              width: SIDEBAR_WIDTH,
              minWidth: SIDEBAR_WIDTH,
              borderRight: '2px solid #e0e0e0',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'white',
              overflowY: 'auto'
            }}
          >
            {sidebarContent}
          </Paper>
        )}

        {/* Drawer para móvil */}
        <Drawer
          anchor="left"
          open={isMobile && sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          sx={{ zIndex: 1250 }}
          PaperProps={{
            sx: { width: SIDEBAR_WIDTH }
          }}
        >
          {sidebarContent}
        </Drawer>

        {/* Content Area */}
        <Box 
          ref={contentRef}
          sx={{ 
            flex: 1, 
            p: { xs: 2, md: 3 }, 
            pt: { xs: 1, md: 2 },
            overflowY: 'auto',
            bgcolor: '#f8fffe'
          }}
        >
          <Container maxWidth="md">
            {activeLesson < course.lessons.length ? (
              <Paper
                elevation={0}
                sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, border: '2px solid #e0e0e0', bgcolor: 'white' }}
              >
                {/* Lesson Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, pb: 3, borderBottom: '2px solid #e0e0e0' }}>
                  <Avatar
                    sx={{ width: 48, height: 48, bgcolor: safeAlpha(COLORS.primary, 0.15), color: COLORS.primary, fontWeight: 800 }}
                  >
                    {activeLesson + 1}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: COLORS.dark }}>
                      {course.lessons[activeLesson].title || `Lección ${activeLesson + 1}`}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#636e72' }}>
                      Lección {activeLesson + 1} de {course.lessons.length}
                    </Typography>
                  </Box>
                </Box>

                {/* Lesson Content */}
                <Box 
                  sx={{ 
                    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 2, my: 2 },
                    '& p': { lineHeight: 1.8, color: COLORS.dark, mb: 2 },
                    '& h1, & h2, & h3, & h4, & h5, & h6': { color: COLORS.dark, fontWeight: 700, mt: 3, mb: 2 },
                    '& ul, & ol': { pl: 3, mb: 2 },
                    '& li': { mb: 1, lineHeight: 1.6 },
                    '& a': { color: COLORS.primary, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }
                  }}
                  dangerouslySetInnerHTML={{ __html: course.lessons[activeLesson].content }} 
                />
              </Paper>
            ) : (
              renderTestQuestion()
            )}
          </Container>
        </Box>
      </Box>

      {/* ========== FOOTER ========== */}
      <Paper
        elevation={8}
        sx={{ 
          borderTop: '2px solid #e0e0e0',
          bgcolor: 'white',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          py: 1.5,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button 
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                setActiveLesson(Math.max(0, activeLesson - 1));
                if (contentRef.current) contentRef.current.scrollTop = 0;
              }} 
              disabled={activeLesson === 0}
              sx={{
                borderColor: COLORS.primary,
                color: COLORS.primary,
                fontWeight: 600,
                borderRadius: 2,
                px: { xs: 2, md: 3 },
                '&:hover': { borderColor: COLORS.primary, bgcolor: safeAlpha(COLORS.primary, 0.08) },
                '&.Mui-disabled': { borderColor: '#e0e0e0', color: '#bdbdbd' }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Anterior</Box>
            </Button>

            {/* Indicador central */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {activeLesson < course.lessons.length ? (
                <Chip
                  icon={<MenuBookIcon />}
                  label={`Lección ${activeLesson + 1}/${course.lessons.length}`}
                  sx={{ bgcolor: safeAlpha(COLORS.primary, 0.1), color: COLORS.primary, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.primary } }}
                />
              ) : (
                <Chip
                  icon={<QuizIcon />}
                  label={`Pregunta ${currentQuestion + 1}/${course.test.questions.length}`}
                  sx={{ bgcolor: safeAlpha(COLORS.purple, 0.1), color: COLORS.purple, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.purple } }}
                />
              )}
            </Box>

            <Button 
              variant="contained" 
              endIcon={<ArrowForwardIcon />}
              onClick={activeLesson === course.lessons.length ? handleNextQuestion : handleComplete}
              disabled={activeLesson === course.lessons.length && !selectedAnswer}
              sx={{
                background: activeLesson === course.lessons.length 
                  ? `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)`
                  : `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                fontWeight: 700,
                borderRadius: 2,
                px: { xs: 2, md: 3 },
                boxShadow: `0 4px 14px ${safeAlpha(COLORS.gradient1, 0.4)}`,
                '&:hover': { boxShadow: `0 6px 20px ${safeAlpha(COLORS.gradient1, 0.5)}` },
                '&.Mui-disabled': { background: '#e0e0e0', color: '#9e9e9e' }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {activeLesson === course.lessons.length 
                  ? (currentQuestion === course.test.questions.length - 1 ? 'Finalizar test' : 'Siguiente pregunta')
                  : 'Siguiente lección'}
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                {activeLesson === course.lessons.length ? 'Siguiente' : 'Siguiente'}
              </Box>
            </Button>
          </Box>
        </Container>
      </Paper>
  
      {/* Results Dialog */}
      <Dialog 
        open={showResults} 
        onClose={handleCloseResults}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            background: testScore >= (course.min_passing_score || 70)
              ? `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`
              : `linear-gradient(135deg, ${COLORS.error} 0%, ${COLORS.orange} 100%)`,
            color: 'white',
            textAlign: 'center',
            py: 4
          }}
        >
          <Box sx={{ mb: 2 }}>
            {testScore >= (course.min_passing_score || 70) ? (
              <EmojiEventsIcon sx={{ fontSize: 64 }} />
            ) : (
              <CancelIcon sx={{ fontSize: 64 }} />
            )}
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            {testScore >= (course.min_passing_score || 70) ? '¡Felicidades!' : 'Sigue intentando'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Box 
            sx={{ 
              width: 120, 
              height: 120, 
              borderRadius: '50%',
              background: testScore >= (course.min_passing_score || 70)
                ? `linear-gradient(135deg, ${safeAlpha(COLORS.success, 0.15)} 0%, ${safeAlpha(COLORS.primary, 0.15)} 100%)`
                : safeAlpha(COLORS.error, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}
          >
            <Typography variant="h2" sx={{ fontWeight: 900, color: testScore >= (course.min_passing_score || 70) ? COLORS.success : COLORS.error }}>
              {Math.round(testScore)}%
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ color: COLORS.dark, mb: 1 }}>
            {testScore >= (course.min_passing_score || 70) 
              ? '¡Has completado el curso satisfactoriamente!' 
              : `Necesitas ${course.min_passing_score || 70}% para aprobar`}
          </Typography>
          <Typography sx={{ color: '#636e72' }}>
            {selectedTestQuestions.filter((q, i) => testAnswers[i] === (q.correct_answer || q.correctAnswer)).length} de {selectedTestQuestions.length} respuestas correctas
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
          <Button onClick={handleCloseResults} sx={{ color: '#636e72' }}>
            Cerrar
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/courses')}
            sx={{ borderColor: COLORS.primary, color: COLORS.primary, fontWeight: 600, borderRadius: 2 }}
          >
            Volver a cursos
          </Button>
          {testScore >= (course.min_passing_score || 70) && (
            <Button
              variant="contained"
              onClick={handleViewCertificate}
              sx={{ background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`, fontWeight: 600, borderRadius: 2 }}
            >
              Ver Certificado
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseViewer;